import React, { useRef } from 'react';
import WalLogoIcon from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { formatAddress } from 'src/utils/CommonFunctions';

import { getBadgeType, getOrderTypeBadge } from './track.utils';

const getShortName = (name: string) =>
    name
        .split(' ')
        .slice(0, 2)
        .map((n) => n[0]?.toUpperCase() ?? '')
        .join('');

const TrackDraggableCell = ({ columnName, data }: { columnName: string; data: any }) => {
    const orgBranding = useOrgBranding();
    const fromAddressRef = useRef<HTMLParagraphElement>(null);
    const toAddressRef = useRef<HTMLParagraphElement>(null);

    if (data === null || data === undefined) return <>-</>;

    const renderAddress = (addressRef: React.RefObject<HTMLParagraphElement>) => {
        const addressText = `${data?.companyName ?? ''}${data?.city ? `, ${formatAddress(data.city)}` : ''}${data?.stateCode ? `, ${data.stateCode}` : ''}${
            data?.postal ? `, ${formatAddress(data.postal)}` : ''
        }`;

        return (
            <TruncateTextCmp message={addressText} refName={addressRef}>
                <p ref={addressRef} className="font-medium truncate text-xs max-w-[160px]">
                    {addressText}
                </p>
            </TruncateTextCmp>
        );
    };

    switch (columnName) {
        case 'Order ID':
            return <span className="font-semibold text-xs text-utilityBlack800 truncate">#{data?.masterOrderFullId || '—'}</span>;

        case 'Carrier': {
            // Org presented as the carrier (masked or unassigned) — show the WAL icon, never initials
            const isOrgCarrier = !data?.carrierName || data.carrierName === orgBranding.name || data.carrierName === 'Western Alliance';

            return (
                <div className="flex items-center gap-x-2 overflow-hidden">
                    {data?.carrierImage ? (
                        <img src={data.carrierImage} className="table-profile-icn-circle-xs rounded-md flex-none" />
                    ) : isOrgCarrier ? (
                        <div className="table-profile-customer-circle-xs rounded-md !bg-utilityBlack flex items-center justify-center p-0.5 flex-none">
                            <img src={WalLogoIcon} alt={orgBranding.name} className="w-full h-full object-contain" />
                        </div>
                    ) : (
                        <div className="table-profile-customer-circle-xs rounded-md flex-none">{data?.carrierName ? getShortName(data.carrierName) : '—'}</div>
                    )}
                    <p className="font-medium text-xs truncate">{data?.carrierName || orgBranding.name}</p>
                </div>
            );
        }

        case 'Type':
            return data?.orderType ? (
                <BadgeCmp style="modern" type={getOrderTypeBadge(data.orderType)}>
                    {data.orderType.toUpperCase()}
                </BadgeCmp>
            ) : (
                <>-</>
            );

        case 'Status':
            return data?.orderStatus ? (
                <BadgeCmp style="modern" type={getBadgeType(data.orderStatus)}>
                    {data.orderStatus.charAt(0).toUpperCase() + data.orderStatus.slice(1).replace(/-/g, ' ')}
                </BadgeCmp>
            ) : (
                <>-</>
            );

        // Schedule columns — values pre-built by formatTrackRowData via getScheduleColumnValues.
        // `date` carries <sup> ordinal HTML from the shared formatter (trusted utility output).
        case 'Pickup Date':
        case 'Delivery Date':
            return <span className="text-xs truncate" dangerouslySetInnerHTML={{ __html: data?.date || '-' }} />;

        case 'Pickup Type':
        case 'Pickup Time':
        case 'Delivery Type':
        case 'Delivery Time':
            return <span className="text-xs truncate">{data?.text || '-'}</span>;

        case 'From':
            return renderAddress(fromAddressRef);

        case 'To':
            return renderAddress(toAddressRef);

        default:
            return <>-</>;
    }
};

export default TrackDraggableCell;
